// src/services/creatorService.ts

import apiClient from '../api/apiClient';
import { normalizeStrapi, formatRecordForStrapi } from '../utils/strapi-normalizer';
import { StrapiResponse } from '@/types/common';
import {
  CreatorDashboard,
  CreatorAnalytics,
  PublicCatalog,
  Transaction,
  RevenueShare,
  LicenseRequest
} from '@/types/marketplace';

/**
 * Creator Service - Quản lý tính năng cho content creators
 * Tích hợp với Vietrro Infrastructure Architecture
 */
class CreatorService {
  private readonly baseUrl = '/api';

  // ========================================
  // DASHBOARD & ANALYTICS
  // ========================================

  /**
   * Lấy dashboard tổng quan cho creator
   */
  async getDashboard(creatorId: string): Promise<CreatorDashboard> {
    try {
      console.log(`📊 Fetching creator dashboard for: ${creatorId}`);

      const response = await apiClient.get<StrapiResponse<CreatorDashboard>>(
        `${this.baseUrl}/creators/${creatorId}/dashboard`,
        {
          params: {
            populate: 'deep'
          }
        }
      );

      const dashboard = normalizeStrapi<CreatorDashboard>(response.data.data);
      console.log('✅ Creator dashboard loaded successfully');

      return dashboard as CreatorDashboard;
    } catch (error) {
      console.error('❌ Failed to fetch creator dashboard:', error);
      throw new Error('Không thể tải dashboard. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy analytics chi tiết
   */
  async getAnalytics(
    creatorId: string,
    period?: { from: string; to: string }
  ): Promise<CreatorAnalytics> {
    try {
      console.log(`📈 Fetching analytics for creator: ${creatorId}`);

      const params: Record<string, string> = {
        'filters[creator][documentId][$eq]': creatorId,
        populate: 'deep'
      };

      if (period) {
        params['filters[createdAt][$gte]'] = period.from;
        params['filters[createdAt][$lte]'] = period.to;
      }

      const response = await apiClient.get<StrapiResponse<CreatorAnalytics>>(
        `${this.baseUrl}/creator-analytics`,
        { params }
      );

      const analytics = normalizeStrapi<CreatorAnalytics>(response.data.data);
      console.log('✅ Analytics loaded successfully');

      return analytics as CreatorAnalytics;
    } catch (error) {
      console.error('❌ Failed to fetch analytics:', error);
      throw new Error('Không thể tải analytics. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy thông tin chia sẻ doanh thu
   */
  async getRevenueShare(
    creatorId: string,
    period?: { from: string; to: string }
  ): Promise<RevenueShare> {
    try {
      console.log(`💰 Fetching revenue share for creator: ${creatorId}`);

      const params: Record<string, string> = {
        'filters[content][owners][documentId][$eq]': creatorId,
        populate: 'deep'
      };

      if (period) {
        params['filters[period][$gte]'] = period.from;
        params['filters[period][$lte]'] = period.to;
      }

      const response = await apiClient.get<StrapiResponse<RevenueShare[]>>(
        `${this.baseUrl}/revenues`,
        { params }
      );

      // Calculate total revenue share
      const revenues = normalizeStrapi<RevenueShare[]>(response.data.data) as RevenueShare[];
      const totalRevenue = revenues?.reduce((sum, rev) => sum + (rev.totalRevenue || 0), 0) || 0;
      const creatorShare = totalRevenue * 0.85; // 85% cho creator
      const platformFee = totalRevenue * 0.15;  // 15% cho platform

      return {
        totalRevenue,
        creatorShare,
        platformFee,
        revenues: revenues || [],
        period: period || { from: '', to: '' }
      } as RevenueShare;
    } catch (error) {
      console.error('❌ Failed to fetch revenue share:', error);
      throw new Error('Không thể tải thông tin doanh thu. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // CONTENT MANAGEMENT
  // ========================================

  /**
   * Lấy danh sách nội dung của creator
   */
  async getCreatorContents(
    creatorId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ data: PublicCatalog[], meta: any }> {
    try {
      console.log(`📋 Fetching creator contents: ${creatorId}, page: ${page}`);

      const result = await apiClient.searchRecords<PublicCatalog>(
        'public-catalog',
        {
          'content[owners][documentId][$eq]': creatorId
        },
        {
          page,
          pageSize
        },
        {
          populate: 'deep',
          sort: 'createdAt:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} contents for creator`);
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to fetch creator contents:', error);
      throw new Error('Không thể tải danh sách nội dung. Vui lòng thử lại sau.');
    }
  }

  /**
   * Đăng tác phẩm lên marketplace
   */
  async publishToMarketplace(
    contentId: string,
    marketplaceData: Partial<PublicCatalog>
  ): Promise<PublicCatalog> {
    try {
      console.log(`🚀 Publishing content to marketplace: ${contentId}`);

      const createData = {
        content: contentId,
        shortDescription: marketplaceData.shortDescription,
        tags: marketplaceData.tags || [],
        licenseOptions: marketplaceData.licenseOptions || {},
        isVisible: true,
        highlightOrder: marketplaceData.highlightOrder || 0,
        viewCount: 0,
        publishedAt: new Date().toISOString()
      };

      const result = await apiClient.createRecord<PublicCatalog>(
        'public-catalog',
        createData
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Content published to marketplace successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to publish to marketplace:', error);
      throw new Error('Không thể đăng tác phẩm lên marketplace. Vui lòng thử lại sau.');
    }
  }

  /**
   * Cập nhật thông tin licensing
   */
  async updateContentLicensing(
    catalogId: string,
    licensingData: any
  ): Promise<PublicCatalog> {
    try {
      console.log(`⚙️ Updating content licensing: ${catalogId}`);

      const result = await apiClient.updateRecordById<PublicCatalog>(
        'public-catalog',
        catalogId,
        {
          licenseOptions: licensingData.licenseOptions,
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Content licensing updated successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to update content licensing:', error);
      throw new Error('Không thể cập nhật licensing. Vui lòng thử lại sau.');
    }
  }

  /**
   * Gỡ nội dung khỏi marketplace
   */
  async removeFromMarketplace(catalogId: string): Promise<void> {
    try {
      console.log(`🗑️ Removing content from marketplace: ${catalogId}`);

      const result = await apiClient.updateRecordById(
        'public-catalog',
        catalogId,
        {
          isVisible: false,
          removedAt: new Date().toISOString()
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Content removed from marketplace successfully');
    } catch (error) {
      console.error('❌ Failed to remove from marketplace:', error);
      throw new Error('Không thể gỡ nội dung khỏi marketplace. Vui lòng thử lại sau.');
    }
  }

  /**
   * Cập nhật thông tin marketplace
   */
  async updateMarketplaceContent(
    catalogId: string,
    updateData: Partial<PublicCatalog>
  ): Promise<PublicCatalog> {
    try {
      console.log(`📝 Updating marketplace content: ${catalogId}`);

      const result = await apiClient.updateRecordById<PublicCatalog>(
        'public-catalog',
        catalogId,
        {
          ...updateData,
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Marketplace content updated successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to update marketplace content:', error);
      throw new Error('Không thể cập nhật thông tin marketplace. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // LICENSE REQUEST MANAGEMENT
  // ========================================

  /**
   * Lấy danh sách yêu cầu license đến
   */
  async getIncomingLicenseRequests(
    creatorId: string,
    page: number = 1,
    status?: string
  ): Promise<{ data: LicenseRequest[], meta: any }> {
    try {
      console.log(`📩 Fetching incoming license requests for creator: ${creatorId}`);

      const filters: Record<string, any> = {
        'content[owners][documentId][$eq]': creatorId
      };

      if (status) {
        filters['licenseRequestStatus[$eq]'] = status;
      }

      const result = await apiClient.searchRecords<LicenseRequest>(
        'license-request',
        filters,
        { page, pageSize: 20 },
        {
          populate: 'deep',
          sort: 'requestDate:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} license requests`);
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to fetch license requests:', error);
      throw new Error('Không thể tải danh sách yêu cầu license. Vui lòng thử lại sau.');
    }
  }

  /**
   * Phê duyệt yêu cầu license
   */
  async approveLicenseRequest(
    requestId: string,
    terms?: any
  ): Promise<LicenseRequest> {
    try {
      console.log(`✅ Approving license request: ${requestId}`);

      const result = await apiClient.updateRecordById<LicenseRequest>(
        'license-request',
        requestId,
        {
          licenseRequestStatus: 'approved',
          responseDate: new Date().toISOString(),
          ...(terms && { approvedTerms: terms }),
          notes: terms?.notes || 'Yêu cầu đã được phê duyệt'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ License request approved successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to approve license request:', error);
      throw new Error('Không thể phê duyệt yêu cầu license. Vui lòng thử lại sau.');
    }
  }

  /**
   * Từ chối yêu cầu license
   */
  async rejectLicenseRequest(
    requestId: string,
    reason: string
  ): Promise<LicenseRequest> {
    try {
      console.log(`❌ Rejecting license request: ${requestId}`);

      const result = await apiClient.updateRecordById<LicenseRequest>(
        'license-request',
        requestId,
        {
          licenseRequestStatus: 'rejected',
          responseDate: new Date().toISOString(),
          notes: reason
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ License request rejected successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to reject license request:', error);
      throw new Error('Không thể từ chối yêu cầu license. Vui lòng thử lại sau.');
    }
  }

  /**
   * Thương lượng yêu cầu license
   */
  async negotiateLicenseRequest(
    requestId: string,
    negotiationData: any
  ): Promise<LicenseRequest> {
    try {
      console.log(`🤝 Negotiating license request: ${requestId}`);

      const result = await apiClient.updateRecordById<LicenseRequest>(
        'license-request',
        requestId,
        {
          licenseRequestStatus: 'pending', // Vẫn pending nhưng có negotiation
          responseDate: new Date().toISOString(),
          notes: negotiationData.message || 'Đề xuất thương lượng điều khoản',
          intendedUse: negotiationData.intendedUse || undefined
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ License negotiation submitted successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to negotiate license request:', error);
      throw new Error('Không thể thương lượng yêu cầu license. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // REVENUE & PAYOUT MANAGEMENT
  // ========================================

  /**
   * Lấy lịch sử payout
   */
  async getPayouts(creatorId: string): Promise<Transaction[]> {
    try {
      console.log(`💳 Fetching payouts for creator: ${creatorId}`);

      const result = await apiClient.searchRecords<Transaction>(
        'payment',
        {
          'receiver[documentId][$eq]': creatorId,
          'paymentStatus[$in]': ['completed', 'pending', 'processing']
        },
        { page: 1, pageSize: 50 },
        {
          populate: 'deep',
          sort: 'createdAt:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} payout records`);
      return result.data?.data || [];
    } catch (error) {
      console.error('❌ Failed to fetch payouts:', error);
      throw new Error('Không thể tải lịch sử payout. Vui lòng thử lại sau.');
    }
  }

  /**
   * Yêu cầu rút tiền
   */
  async requestPayout(
    amount: number,
    paymentMethod: string,
    bankAccount?: any
  ): Promise<Transaction> {
    try {
      console.log(`💰 Requesting payout: ${amount} VND via ${paymentMethod}`);

      if (amount < 100000) { // Minimum 100,000 VND
        throw new Error('Số tiền rút tối thiểu là 100,000 VND');
      }

      const result = await apiClient.createRecord<Transaction>(
        'payment',
        {
          amount,
          currency: 'VND',
          paymentMethod,
          paymentStatus: 'pending',
          description: `Yêu cầu rút tiền ${amount.toLocaleString('vi-VN')} VND`,
          ...(bankAccount && { billingAddress: bankAccount }),
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Payout request created successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to request payout:', error);
      throw new Error('Không thể tạo yêu cầu rút tiền. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy báo cáo thu nhập
   */
  async getEarningsReport(
    creatorId: string,
    period?: { from: string; to: string }
  ): Promise<any> {
    try {
      console.log(`📊 Generating earnings report for creator: ${creatorId}`);

      // Lấy dữ liệu revenue
      const revenueResult = await apiClient.searchRecords(
        'revenue',
        {
          'content[owners][documentId][$eq]': creatorId,
          ...(period && {
            'period[$gte]': period.from,
            'period[$lte]': period.to
          })
        },
        { page: 1, pageSize: 100 },
        { populate: 'deep' }
      );

      // Lấy dữ liệu payments
      const paymentResult = await apiClient.searchRecords(
        'payment',
        {
          'receiver[documentId][$eq]': creatorId,
          'paymentStatus': 'completed',
          ...(period && {
            'confirmedAt[$gte]': period.from,
            'confirmedAt[$lte]': period.to
          })
        },
        { page: 1, pageSize: 100 }
      );

      const revenues = revenueResult.data?.data || [];
      const payments = paymentResult.data?.data || [];

      // Tính toán thống kê
      const totalEarnings = revenues.reduce((sum: number, rev: any) =>
        sum + (rev.totalRevenue || 0), 0
      );

      const creatorShare = totalEarnings * 0.85;
      const platformFee = totalEarnings * 0.15;
      const totalPaidOut = payments.reduce((sum: number, payment: any) =>
        sum + (payment.amount || 0), 0
      );

      const pendingBalance = creatorShare - totalPaidOut;

      const report = {
        period: period || { from: '', to: '' },
        totalEarnings,
        creatorShare,
        platformFee,
        totalPaidOut,
        pendingBalance,
        revenueBreakdown: revenues,
        paymentHistory: payments,
        generatedAt: new Date().toISOString()
      };

      console.log('✅ Earnings report generated successfully');
      return report;
    } catch (error) {
      console.error('❌ Failed to generate earnings report:', error);
      throw new Error('Không thể tạo báo cáo thu nhập. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // BULK OPERATIONS
  // ========================================

  /**
   * Cập nhật giá hàng loạt
   */
  async bulkUpdatePricing(
    contentIds: string[],
    pricingData: any
  ): Promise<PublicCatalog[]> {
    try {
      console.log(`💵 Bulk updating pricing for ${contentIds.length} contents`);

      const result = await apiClient.bulkUpdateRecords<PublicCatalog>(
        'public-catalog',
        contentIds,
        {
          licenseOptions: pricingData,
          
        }
      );

      const successful = result.results.filter(r => r.success);
      console.log(`✅ Successfully updated ${successful.length}/${contentIds.length} contents`);

      return successful.map(r => r.data).filter(Boolean) as PublicCatalog[];
    } catch (error) {
      console.error('❌ Failed to bulk update pricing:', error);
      throw new Error('Không thể cập nhật giá hàng loạt. Vui lòng thử lại sau.');
    }
  }

  /**
   * Đăng hàng loạt lên marketplace
   */
  async bulkPublishToMarketplace(
    contentIds: string[],
    marketplaceData: any
  ): Promise<PublicCatalog[]> {
    try {
      console.log(`🚀 Bulk publishing ${contentIds.length} contents to marketplace`);

      const results: PublicCatalog[] = [];

      // Process in chunks to avoid overwhelming the server
      const chunkSize = 5;
      for (let i = 0; i < contentIds.length; i += chunkSize) {
        const chunk = contentIds.slice(i, i + chunkSize);

        const chunkPromises = chunk.map(contentId =>
          this.publishToMarketplace(contentId, {
            ...marketplaceData,
            content: contentId
          }).catch(error => {
            console.warn(`Failed to publish content ${contentId}:`, error);
            return null;
          })
        );

        const chunkResults = await Promise.all(chunkPromises);
        results.push(...chunkResults.filter(Boolean) as PublicCatalog[]);

        // Small delay between chunks
        if (i + chunkSize < contentIds.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      console.log(`✅ Successfully published ${results.length}/${contentIds.length} contents`);
      return results;
    } catch (error) {
      console.error('❌ Failed to bulk publish:', error);
      throw new Error('Không thể đăng hàng loạt lên marketplace. Vui lòng thử lại sau.');
    }
  }

  /**
   * Cập nhật visibility hàng loạt
   */
  async bulkUpdateVisibility(
    contentIds: string[],
    isVisible: boolean
  ): Promise<void> {
    try {
      console.log(`👁️ Bulk updating visibility for ${contentIds.length} contents`);

      await apiClient.bulkUpdateRecords(
        'public-catalog',
        contentIds,
        {
          isVisible,
          
        }
      );

      console.log('✅ Bulk visibility update completed');
    } catch (error) {
      console.error('❌ Failed to bulk update visibility:', error);
      throw new Error('Không thể cập nhật hiển thị hàng loạt. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // OPTIMIZATION & INSIGHTS
  // ========================================

  /**
   * Lấy gợi ý tối ưu hóa
   */
  async getOptimizationSuggestions(contentId: string): Promise<any[]> {
    try {
      console.log(`💡 Getting optimization suggestions for content: ${contentId}`);

      // Fetch content analytics
      const response = await apiClient.get(`${this.baseUrl}/contents/${contentId}?populate=deep`);
      const content = normalizeStrapi(response.data.data);

      // Generate suggestions based on content data
      const suggestions = [];

      if (!content.tags || content.tags.length < 3) {
        suggestions.push({
          type: 'tags',
          priority: 'high',
          message: 'Thêm ít nhất 3-5 tags để tăng khả năng tìm kiếm',
          action: 'add_tags'
        });
      }

      if (!content.description || content.description.length < 100) {
        suggestions.push({
          type: 'description',
          priority: 'medium',
          message: 'Mô tả nên có ít nhất 100 ký tự để tăng SEO',
          action: 'improve_description'
        });
      }

      if (content.viewCount < 10) {
        suggestions.push({
          type: 'promotion',
          priority: 'medium',
          message: 'Chia sẻ tác phẩm trên mạng xã hội để tăng lượt xem',
          action: 'promote_content'
        });
      }

      console.log(`✅ Generated ${suggestions.length} optimization suggestions`);
      return suggestions;
    } catch (error) {
      console.error('❌ Failed to get optimization suggestions:', error);
      throw new Error('Không thể tải gợi ý tối ưu hóa. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy insights thị trường
   */
  async getMarketInsights(category?: string): Promise<any> {
    try {
      console.log(`📈 Fetching market insights${category ? ` for category: ${category}` : ''}`);

      const filters: Record<string, any> = {
        isVisible: true
      };

      if (category) {
        filters['content[category][code][$eq]'] = category;
      }

      const result = await apiClient.searchRecords(
        'public-catalog',
        filters,
        { page: 1, pageSize: 100 },
        {
          populate: 'content.category',
          sort: 'viewCount:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      const contents = result.data?.data || [];

      // Calculate insights
      const totalViews = contents.reduce((sum: number, content: any) =>
        sum + (content.viewCount || 0), 0
      );

      const avgViews = contents.length > 0 ? totalViews / contents.length : 0;
      const topPerforming = contents.slice(0, 10);

      // Tag analysis
      const tagCounts: Record<string, number> = {};
      contents.forEach((content: any) => {
        if (content.tags) {
          content.tags.forEach((tag: string) => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        }
      });

      const trendingTags = Object.entries(tagCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 20)
        .map(([tag, count]) => ({ tag, count }));

      const insights = {
        category,
        totalContents: contents.length,
        totalViews,
        averageViews: Math.round(avgViews),
        topPerforming,
        trendingTags,
        generatedAt: new Date().toISOString()
      };

      console.log('✅ Market insights generated successfully');
      return insights;
    } catch (error) {
      console.error('❌ Failed to get market insights:', error);
      throw new Error('Không thể tải insights thị trường. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy trending tags
   */
  async getTrendingTags(category?: string, limit: number = 20): Promise<string[]> {
    try {
      console.log(`🏷️ Fetching trending tags${category ? ` for category: ${category}` : ''}`);

      const insights = await this.getMarketInsights(category);
      const trendingTags = insights.trendingTags
        .slice(0, limit)
        .map((item: any) => item.tag);

      console.log(`✅ Found ${trendingTags.length} trending tags`);
      return trendingTags;
    } catch (error) {
      console.error('❌ Failed to get trending tags:', error);
      throw new Error('Không thể tải trending tags. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Validate creator permissions
   */
  private validateCreatorAccess(creatorId: string, currentUserId?: string): boolean {
    if (!currentUserId) {
      console.warn('⚠️ No current user ID provided for validation');
      return false;
    }

    if (creatorId !== currentUserId) {
      console.warn('⚠️ Creator ID does not match current user ID');
      return false;
    }

    return true;
  }

  /**
   * Format currency for Vietnamese locale
   */
  private formatCurrency(amount: number, currency: string = 'VND'): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency
    }).format(amount);
  }

  /**
   * Calculate creator share from total revenue
   */
  private calculateCreatorShare(totalRevenue: number): number {
    return Math.round(totalRevenue * 0.85);
  }

  /**
   * Calculate platform fee from total revenue
   */
  private calculatePlatformFee(totalRevenue: number): number {
    return Math.round(totalRevenue * 0.15);
  }
}

// Export singleton instance
export const creatorService = new CreatorService();
export default creatorService;
